/**
 * Permission Engine - Role-based access control enforced at runtime.
 *
 * Provides:
 * - Per-collection CRUD permissions
 * - Per-action execution permissions
 * - Per-field read/write permissions (extensible)
 * - Role-based evaluation
 *
 * Permissions are declared in schema and enforced here - never UI-only.
 *
 * Extends: NuvraSchema ModelPermissions, eventBus
 */

import type { ModelPermissions, SchemaAction } from "@/core/schema/nuvraSchema";
import { eventBus } from "@/runtime/eventBus";
import { logger } from "@/system/logger";

// ─── Types ───

export type CrudOperation = "read" | "create" | "update" | "delete";

export interface PermissionCheckResult {
  allowed: boolean;
  reason?: string;
}

export interface UserContext {
  userId: string;
  roles: string[];
}

// ─── Permission Engine ───

class PermissionEngine {
  private modelPermissions = new Map<string, ModelPermissions>();
  private actionPermissions = new Map<string, string[]>();
  private currentUser: UserContext = { userId: "anonymous", roles: ["public"] };
  private enabled = true;

  /**
   * Register model permissions from schema.
   */
  registerModelPermissions(modelName: string, permissions: ModelPermissions): void {
    this.modelPermissions.set(modelName, permissions);
  }

  /**
   * Register action permissions from schema.
   */
  registerActionPermissions(actionId: string, allowedRoles: string[]): void {
    this.actionPermissions.set(actionId, allowedRoles);
  }

  /**
   * Set the current user context (from auth system).
   */
  setUser(user: UserContext): void {
    this.currentUser = user;
    logger.info("Permission context updated", { userId: user.userId, roles: user.roles });
  }

  /**
   * Get the current user context.
   */
  getUser(): UserContext {
    return this.currentUser;
  }

  /**
   * Enable or disable permission enforcement (e.g., for admin bypass).
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Check if current user can perform a CRUD operation on a collection.
   */
  checkCollectionAccess(collectionName: string, operation: CrudOperation): PermissionCheckResult {
    if (!this.enabled) return { allowed: true };

    const perms = this.modelPermissions.get(collectionName);
    if (!perms) return { allowed: true }; // No permissions defined = open access

    const allowedRoles = perms[operation];
    if (!allowedRoles || allowedRoles.length === 0) return { allowed: true };

    const hasRole = this.currentUser.roles.some(r => allowedRoles.includes(r) || r === "admin");
    if (!hasRole) {
      const reason = `User lacks role for ${operation} on "${collectionName}". Required: ${allowedRoles.join(", ")}`;
      logger.warn("Permission denied", { collection: collectionName, operation, user: this.currentUser.userId });
      return { allowed: false, reason };
    }

    return { allowed: true };
  }

  /**
   * Check if current user can execute an action.
   */
  checkActionAccess(actionId: string): PermissionCheckResult {
    if (!this.enabled) return { allowed: true };

    const allowedRoles = this.actionPermissions.get(actionId);
    if (!allowedRoles || allowedRoles.length === 0) return { allowed: true };

    const hasRole = this.currentUser.roles.some(r => allowedRoles.includes(r) || r === "admin");
    if (!hasRole) {
      return { allowed: false, reason: `User lacks role for action "${actionId}". Required: ${allowedRoles.join(", ")}` };
    }

    return { allowed: true };
  }

  /**
   * Check per-field access.
   */
  checkFieldAccess(collectionName: string, fieldName: string, mode: "read" | "write"): PermissionCheckResult {
    if (!this.enabled) return { allowed: true };

    const perms = this.modelPermissions.get(collectionName);
    if (!perms) return { allowed: true };

    const fieldPerms = mode === "read" ? perms.fieldRead : perms.fieldWrite;
    if (!fieldPerms || !fieldPerms[fieldName]) return { allowed: true };

    const allowedRoles = fieldPerms[fieldName];
    const hasRole = this.currentUser.roles.some(r => allowedRoles.includes(r) || r === "admin");
    if (!hasRole) {
      return { allowed: false, reason: `User lacks ${mode} access to field "${fieldName}" on "${collectionName}"` };
    }

    return { allowed: true };
  }

  /**
   * Teardown - clear all permissions.
   */
  teardown(): void {
    this.modelPermissions.clear();
    this.actionPermissions.clear();
    this.currentUser = { userId: "anonymous", roles: ["public"] };
  }
}

export const permissionEngine = new PermissionEngine();
